import { useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { format, addDays, subDays, addMonths, subMonths, startOfMonth, endOfMonth, isToday, isTomorrow, parseISO } from 'date-fns'
import { getDateLocale } from '@/lib/utils/date'
import { Plus, List, Clock, Calendar, Search, ChevronLeft, ChevronRight, ArrowRight, ChevronDown } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { BookingCard } from '@/components/bookings/BookingCard'
import { BookingTimeline } from '@/components/bookings/BookingTimeline'
import { BookingForm } from '@/components/bookings/BookingForm'
import { WalkInModal } from '@/components/bookings/WalkInModal'
import { BookingDetailPanel } from '@/components/bookings/BookingDetailPanel'
import { Button } from '@/components/ui/Button'
import { Skeleton } from '@/components/ui/Skeleton'
import { DatePicker } from '@/components/ui/DatePicker'
import { useBookings } from '@/lib/queries/useBookings'
import { useAreas } from '@/lib/booking/tableAdapter'
import { useBookingOpeningHours } from '@/lib/booking/scheduleAdapter'
import { useDebounce } from '@/hooks/useDebounce'
import { useUIStore } from '@/lib/store/useUIStore'
import { staggerContainer } from '@/lib/motion'
import type { BookingStatus } from '@/lib/types/booking'

type ViewMode = 'list' | 'timeline' | 'calendar'

const today = new Date()
const EMPTY: never[] = []

const navBtn =
  'flex h-10 w-10 items-center justify-center rounded-[var(--radius-md)] text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)] sm:h-7 sm:w-7 sm:rounded-[var(--radius-sm)]'

export default function BookingsPage() {
  const { t } = useTranslation()
  const {
    bookingsView: view, setBookingsView: setView,
    bookingsListFrom, setBookingsListFrom,
    bookingsListTo, setBookingsListTo,
    bookingsTimelineDate, setBookingsTimelineDate,
    bookingsCalendarMonth, setBookingsCalendarMonth,
  } = useUIStore()

  const listFrom = new Date(bookingsListFrom + 'T12:00:00')
  const listTo = new Date(bookingsListTo + 'T12:00:00')
  const timelineDate = new Date(bookingsTimelineDate + 'T12:00:00')
  const calendarMonth = startOfMonth(new Date(bookingsCalendarMonth + 'T12:00:00'))

  const setListFrom = (d: Date) => setBookingsListFrom(format(d, 'yyyy-MM-dd'))
  const setListTo = (d: Date) => setBookingsListTo(format(d, 'yyyy-MM-dd'))
  const setTimelineDate = (d: Date) => setBookingsTimelineDate(format(d, 'yyyy-MM-dd'))
  const setCalendarMonth = (d: Date) => setBookingsCalendarMonth(format(startOfMonth(d), 'yyyy-MM-dd'))

  const [search, setSearch] = useState('')
  const [statusFilter] = useState<BookingStatus[]>([])
  const [showForm, setShowForm] = useState(false)
  const [showWalkIn, setShowWalkIn] = useState(false)
  const [formDefaults, setFormDefaults] = useState<{ date?: string; time?: string; duration?: number; tableIds?: string[] } | undefined>()
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null)
  const [clearPreviewSignal, setClearPreviewSignal] = useState(0)
  const bumpClearPreview = () => setClearPreviewSignal((s) => s + 1)
  const debouncedSearch = useDebounce(search)

  // ── Query filters derived from view ─────────────────────────────────────
  const handleCreateBooking = (tableDocumentId: string, time: string, duration: number) => {
    setFormDefaults({ date: format(timelineDate, 'yyyy-MM-dd'), time, duration, tableIds: [tableDocumentId] })
    setShowForm(true)
  }

  const bookingFilters = useMemo(() => {
    if (view === 'timeline') {
      return { date: format(timelineDate, 'yyyy-MM-dd') }
    }
    if (view === 'calendar') {
      return {
        dateFrom: format(calendarMonth, 'yyyy-MM-dd'),
        dateTo: format(endOfMonth(calendarMonth), 'yyyy-MM-dd'),
      }
    }
    // list
    return {
      dateFrom: format(listFrom, 'yyyy-MM-dd'),
      dateTo: format(listTo, 'yyyy-MM-dd'),
    }
  }, [view, listFrom, listTo, timelineDate, calendarMonth])

  const { data: bookingsResp, isLoading } = useBookings({
    ...bookingFilters,
    search: debouncedSearch || undefined,
    status: statusFilter.length ? statusFilter : undefined,
  })
  const { data: areasResp } = useAreas()
  const { data: ohResp } = useBookingOpeningHours()

  const bookings     = bookingsResp?.data ?? EMPTY
  const areas        = areasResp?.data   ?? EMPTY
  const openingHours = ohResp?.data      ?? EMPTY

  // ── Calendar grid ────────────────────────────────────────────────────────
  const calendarDays = useMemo(() => {
    const first = calendarMonth
    const last = endOfMonth(calendarMonth)
    // Monday-based offset (0=Mon … 6=Sun)
    const offset = (first.getDay() + 6) % 7
    const days: (Date | null)[] = Array(offset).fill(null)
    for (let d = new Date(first); d <= last; d = addDays(d, 1)) {
      days.push(new Date(d))
    }
    // Pad to full weeks
    while (days.length % 7 !== 0) days.push(null)
    return days
  }, [calendarMonth])

  const bookingsByDate = useMemo(() => {
    const map: Record<string, typeof bookings> = {}
    for (const b of bookings) {
      if (!map[b.date]) map[b.date] = []
      map[b.date].push(b)
    }
    return map
  }, [bookings])

  const sortedDates = useMemo(() => Object.keys(bookingsByDate).sort(), [bookingsByDate])

  // All dates start expanded; user can collapse individual ones
  const [collapsedDates, setCollapsedDates] = useState<Set<string>>(new Set())
  const toggleDate = useCallback((date: string) => {
    setCollapsedDates((prev) => {
      const next = new Set(prev)
      if (next.has(date)) next.delete(date)
      else next.add(date)
      return next
    })
  }, [])

  // ── Page description ─────────────────────────────────────────────────────
  const description = useMemo(() => {
    const locale = getDateLocale()
    if (view === 'timeline') return `${format(timelineDate, 'EEEE, MMMM d', { locale })} — ${t('bookings.reservations', { count: bookings.length })}`
    if (view === 'calendar') return format(calendarMonth, 'MMMM yyyy', { locale })
    const from = format(listFrom, 'MMM d', { locale })
    const to = format(listTo, 'MMM d', { locale })
    return from === to
      ? `${format(listFrom, 'EEEE, MMMM d', { locale })} — ${t('bookings.reservations', { count: bookings.length })}`
      : `${from} – ${to} — ${t('bookings.reservations', { count: bookings.length })}`
  }, [view, listFrom, listTo, timelineDate, calendarMonth, bookings.length, t])

  const viewButtons: Array<{ mode: ViewMode; icon: React.ElementType; label: string }> = [
    { mode: 'list', icon: List, label: t('bookings.views.list') },
    { mode: 'timeline', icon: Clock, label: t('bookings.views.timeline') },
    { mode: 'calendar', icon: Calendar, label: t('bookings.views.calendar') },
  ]

  return (
    <div>
      <PageHeader
        title={t('bookings.title')}
        description={description}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => setShowWalkIn(true)}>
              Walk-in
            </Button>
            <Button onClick={() => setShowForm(true)}>
              <Plus className="h-4 w-4" /> {t('bookings.newBooking')}
            </Button>
          </div>
        }
      />

      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div className="mb-6 flex min-w-0 flex-wrap items-center gap-3">

        {/* View switcher */}
        <div className="flex items-center gap-1 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] p-1">
          {viewButtons.map(({ mode, icon: Icon, label }) => (
            <button
              key={mode}
              onClick={() => setView(mode)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[var(--radius-sm)] text-xs font-medium transition-colors cursor-pointer ${
                view === mode
                  ? 'bg-[var(--color-accent-amber-dim)] text-[var(--color-accent-amber)]'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
              }`}
            >
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>

        {/* ── List: from → to date range ── */}
        {view === 'list' && (
          <div className="flex w-full min-w-0 flex-wrap items-center gap-2 sm:w-auto sm:flex-nowrap">
            <div className="w-full min-w-0 sm:w-44">
              <DatePicker
                value={format(listFrom, 'yyyy-MM-dd')}
                onChange={(v) => {
                  const d = new Date(v + 'T12:00:00')
                  setListFrom(d)
                  if (d > listTo) setListTo(d)
                }}
              />
            </div>
            <ArrowRight className="h-3.5 w-3.5 text-[var(--color-text-muted)] shrink-0" />
            <div className="w-full min-w-0 sm:w-44">
              <DatePicker
                value={format(listTo, 'yyyy-MM-dd')}
                onChange={(v) => {
                  const d = new Date(v + 'T12:00:00')
                  setListTo(d)
                  if (d < listFrom) setListFrom(d)
                }}
              />
            </div>
          </div>
        )}

        {/* ── Timeline: prev / date / next / Today ── */}
        {view === 'timeline' && (
          <div className="flex w-full min-w-0 items-center gap-1 sm:w-auto">
            <button type="button" className={navBtn} onClick={() => setTimelineDate(subDays(timelineDate, 1))} aria-label="Previous day">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="min-w-0 flex-1 sm:w-52 sm:flex-none">
              <DatePicker
                value={format(timelineDate, 'yyyy-MM-dd')}
                onChange={(v) => setTimelineDate(new Date(v + 'T12:00:00'))}
              />
            </div>
            <button type="button" className={navBtn} onClick={() => setTimelineDate(addDays(timelineDate, 1))} aria-label="Next day">
              <ChevronRight className="h-4 w-4" />
            </button>
            {!isToday(timelineDate) && (
              <button
                onClick={() => setTimelineDate(today)}
                className="ml-1 text-xs text-[var(--color-accent-amber)] hover:underline"
                type="button"
              >
                {t('bookings.today')}
              </button>
            )}
          </div>
        )}

        {/* ── Calendar: prev / Month Year / next ── */}
        {view === 'calendar' && (
          <div className="flex items-center gap-1">
            <button type="button" className={navBtn} onClick={() => setCalendarMonth(startOfMonth(subMonths(calendarMonth, 1)))} aria-label="Previous month">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-3 text-sm font-medium text-[var(--color-text-primary)] min-w-[120px] text-center">
              {format(calendarMonth, 'MMMM yyyy', { locale: getDateLocale() })}
            </span>
            <button type="button" className={navBtn} onClick={() => setCalendarMonth(startOfMonth(addMonths(calendarMonth, 1)))} aria-label="Next month">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Search (list only) */}
        {view === 'list' && (
          <div className="flex-1 max-w-xs">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--color-text-muted)]" />
              <input
                placeholder={t('bookings.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full h-9 pl-9 pr-3 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] border border-[var(--color-border-default)] focus:outline-none focus:border-[var(--color-accent-amber)]/50"
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Content ──────────────────────────────────────────────────────── */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={72} />
          ))}
        </div>
      ) : view === 'list' ? (
        bookings.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Calendar className="h-12 w-12 text-[var(--color-text-muted)] mb-4" />
            <p className="text-[var(--color-text-secondary)] mb-1">{t('bookings.noBookingsFound')}</p>
            <p className="text-sm text-[var(--color-text-muted)]">{t('bookings.noBookingsHint')}</p>
          </div>
        ) : (
          <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-2">
            {sortedDates.map((dateStr) => {
              const dayBookings = bookingsByDate[dateStr]
              const date = parseISO(dateStr)
              const isExpanded = !collapsedDates.has(dateStr)
              const dateLabel = isToday(date)
                ? `${t('bookings.today')} · ${format(date, 'EEEE, MMMM d', { locale: getDateLocale() })}`
                : isTomorrow(date)
                ? `${t('bookings.tomorrow')} · ${format(date, 'EEEE, MMMM d', { locale: getDateLocale() })}`
                : format(date, 'EEEE, MMMM d', { locale: getDateLocale() })

              return (
                <div
                  key={dateStr}
                  className="rounded-[var(--radius-lg)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] overflow-hidden"
                >
                  {/* Accordion header */}
                  <button
                    onClick={() => toggleDate(dateStr)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-[var(--color-bg-elevated)] transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-semibold ${isToday(date) ? 'text-[var(--color-accent-amber)]' : 'text-[var(--color-text-primary)]'}`}>
                        {dateLabel}
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] font-medium">
                        {t('dashboard.bookings', { count: dayBookings.length })}
                      </span>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-[var(--color-text-muted)] transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Accordion body */}
                  <AnimatePresence initial={false}>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: 'easeInOut' }}
                        style={{ overflow: 'hidden' }}
                      >
                        <div className="px-3 pb-3 space-y-2">
                          {dayBookings.map((b) => (
                            <BookingCard key={b.documentId} booking={b} onClick={() => setSelectedDocumentId(b.documentId)} />
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            })}
          </motion.div>
        )
      ) : view === 'timeline' ? (
        <BookingTimeline
          bookings={bookings}
          areas={areas}
          openingHours={openingHours}
          date={format(timelineDate, 'yyyy-MM-dd')}
          onBookingClick={(documentId) => setSelectedDocumentId(documentId)}
          onCreateBooking={handleCreateBooking}
          clearPreviewSignal={clearPreviewSignal}
        />
      ) : (
        /* Calendar grid */
        <div>
          <div className="grid grid-cols-7 gap-px mb-1">
            {(
              [
                t('bookings.days.mon'),
                t('bookings.days.tue'),
                t('bookings.days.wed'),
                t('bookings.days.thu'),
                t('bookings.days.fri'),
                t('bookings.days.sat'),
                t('bookings.days.sun'),
              ] as string[]
            ).map((d) => (
              <div key={d} className="text-center text-xs text-[var(--color-text-muted)] py-2 font-medium">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-px border border-[var(--color-border-subtle)] rounded-[var(--radius-lg)] overflow-hidden">
            {calendarDays.map((day, i) => {
              if (day === null) {
                return <div key={`empty-${i}`} className="min-h-[68px] bg-[var(--color-bg-elevated)]/30 sm:min-h-[100px]" />
              }
              const dateStr = format(day, 'yyyy-MM-dd')
              const dayBookings = bookingsByDate[dateStr] ?? []
              const visible = dayBookings.slice(0, 3)
              const overflow = dayBookings.length - visible.length
              return (
                <button
                  key={day.toISOString()}
                  onClick={() => { setTimelineDate(day); setView('timeline') }}
                  className={`flex min-h-[68px] cursor-pointer flex-col gap-1 bg-[var(--color-bg-base)] p-1 text-left transition-colors hover:bg-[var(--color-bg-elevated)] sm:min-h-[100px] sm:p-2 ${
                    isToday(day) ? 'ring-1 ring-inset ring-[var(--color-accent-amber)]' : ''
                  }`}
                >
                  <span className={`text-[10px] font-semibold sm:text-xs ${isToday(day) ? 'text-[var(--color-accent-amber)]' : 'text-[var(--color-text-muted)]'}`}>
                    {day.getDate()}
                  </span>
                  {visible.map((b) => (
                    <span
                      key={b.documentId}
                      className="hidden w-full truncate rounded bg-[var(--color-accent-amber-dim)] px-1 py-0.5 text-[0.68rem] font-medium leading-tight text-[var(--color-accent-amber)] sm:block"
                    >
                      {b.time ? `${b.time} ` : ''}{b.guest?.name ?? '—'}
                    </span>
                  ))}
                  {dayBookings.length > 0 && (
                    <span className="mt-auto flex items-center gap-0.5 py-0.5 sm:hidden" aria-hidden="true">
                      {dayBookings.slice(0, 3).map((booking) => (
                        <span key={booking.documentId} className="h-1.5 w-1.5 rounded-full bg-[var(--color-accent-amber)]" />
                      ))}
                    </span>
                  )}
                  {overflow > 0 && (
                    <span className="pl-0.5 text-[0.6rem] text-[var(--color-text-muted)] sm:text-[0.65rem]">
                      +{overflow}<span className="hidden sm:inline"> more</span>
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}

      <BookingForm
        open={showForm}
        onClose={() => { setShowForm(false); setFormDefaults(undefined); bumpClearPreview() }}
        defaults={formDefaults}
      />

      <WalkInModal
        open={showWalkIn}
        onClose={() => { setShowWalkIn(false); bumpClearPreview() }}
      />
      <BookingDetailPanel documentId={selectedDocumentId} onClose={() => setSelectedDocumentId(null)} />
    </div>
  )
}
